import { QueryClient } from 'react-query'
import { optimisticUpdateFileInInfiniteLists, updateFileDetailCache } from 'hooks/queries/files/cacheUtils'
import { AlmanacFileShowItem } from 'hooks/queries/files/types'

export interface PreviewChannelPreviewUpdate {
  op: 'preview_update'
  image_url: string
  last_rendered_version: number
  duration?: number | null
}

export interface PreviewChannelVersionUpdate {
  op: 'version_update'
  version: number
}

export interface PreviewChannelContentUpdate {
  op: 'content_update'
  design_content?: object
  video_content?: object
}

export type PreviewChannelMessage =
  | PreviewChannelPreviewUpdate
  | PreviewChannelVersionUpdate
  | PreviewChannelContentUpdate

const PREVIEW_SYNC_ASSET_TYPES = new Set(['Design', 'Video'])

export const isPreviewSyncAssetType = (assetType?: string | null) =>
  !!assetType && PREVIEW_SYNC_ASSET_TYPES.has(assetType)

export const applyPreviewChannelPreviewUpdate = (
  queryClient: QueryClient,
  workspaceId: number,
  fileKey: string,
  data: PreviewChannelPreviewUpdate,
) => {
  optimisticUpdateFileInInfiniteLists(queryClient, workspaceId, fileKey, (f) => ({
    ...f,
    image_url: data.image_url,
    image_url_generating: false,
    duration: data.duration ?? f.duration,
    is_generating: false,
  }))

  updateFileDetailCache<AlmanacFileShowItem>(queryClient, workspaceId, fileKey, (file) => ({
    ...file,
    image_url: data.image_url,
    preview_url: data.image_url,
    image_url_generating: false,
    duration: data.duration ?? file.duration,
    is_generating: false,
    last_rendered_version: data.last_rendered_version,
    version: Math.max(file.version ?? 0, data.last_rendered_version),
  }))
}

export const applyPreviewChannelVersionUpdate = (
  queryClient: QueryClient,
  workspaceId: number,
  fileKey: string,
  data: PreviewChannelVersionUpdate,
) => {
  optimisticUpdateFileInInfiniteLists(queryClient, workspaceId, fileKey, (f) => ({
    ...f,
    image_url_generating: true,
  }))

  updateFileDetailCache<AlmanacFileShowItem>(queryClient, workspaceId, fileKey, (file) => ({
    ...file,
    version: data.version,
    image_url_generating: true,
  }))
}

export const applyPreviewChannelContentUpdate = (
  queryClient: QueryClient,
  workspaceId: number,
  fileKey: string,
  data: PreviewChannelContentUpdate,
) => {
  if (data.design_content) {
    const designContent = data.design_content
    optimisticUpdateFileInInfiniteLists(queryClient, workspaceId, fileKey, (f) => ({
      ...f,
      design_content: designContent,
    }))

    updateFileDetailCache<AlmanacFileShowItem>(queryClient, workspaceId, fileKey, (file) => ({
      ...file,
      design_content: designContent,
    }))
    return
  }

  if (data.video_content) {
    // Video content is not rendered in list cards, so only the detail cache needs updating.
    updateFileDetailCache(queryClient, workspaceId, fileKey, (file) => ({
      ...file,
      content: data.video_content,
    }))
  }
}

export const handlePreviewChannelMessage = (
  queryClient: QueryClient,
  workspaceId: number,
  fileKey: string,
  data: PreviewChannelMessage,
) => {
  if (data.op === 'preview_update') {
    applyPreviewChannelPreviewUpdate(queryClient, workspaceId, fileKey, data)
    return
  }

  if (data.op === 'version_update') {
    applyPreviewChannelVersionUpdate(queryClient, workspaceId, fileKey, data)
    return
  }

  if (data.op === 'content_update') {
    applyPreviewChannelContentUpdate(queryClient, workspaceId, fileKey, data)
  }
}

export const isPreviewRendering = (version?: number, lastRenderedVersion?: number) =>
  version !== undefined && lastRenderedVersion !== undefined && lastRenderedVersion < version
